import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Text, Group, Button, Stack, Loader, Flex, Select, TextInput, Badge } from '@mantine/core';
import { Copy, Check, Plus } from '@phosphor-icons/react';
import api from '../utils/api';
import { formatMoney } from '../utils/money';

const STANDARD_EVENTS = [
  'CompleteRegistration', 'Lead', 'Purchase', 'StartTrial', 'Subscribe',
  'Contact', 'AddToCart', 'InitiateCheckout',
];

const snippetFor = (pixelId) => `<!-- Meta Pixel -->
<script>
!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${pixelId}');
fbq('track', 'PageView');
</script>
<!-- End Meta Pixel -->`;

/**
 * Zakładka Konwersje dla kampanii Meta: status Pixela (z aktywnością zdarzeń
 * z 7 dni), konwersje per reklama TEJ kampanii oraz zarządzanie
 * niestandardowymi konwersjami. Pixel i konwersje to obiekty konta
 * reklamowego (semantyka Meta), ale obsługujemy je w kontekście kampanii.
 */
export default function MetaConversionsPanel({ campaignId, notify, currency = 'PLN' }) {
  const { t } = useTranslation();
  const [pixels, setPixels] = useState(null);
  const [conversions, setConversions] = useState([]);
  const [adStats, setAdStats] = useState([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showSnippet, setShowSnippet] = useState(null);
  const [showConvForm, setShowConvForm] = useState(false);
  const [convForm, setConvForm] = useState({ name: '', pixel_id: '', event: '', url_contains: '' });
  const [savingConv, setSavingConv] = useState(false);

  const toast = (msg, type) => { if (notify) notify(msg, type); };

  const load = () => {
    Promise.all([
      api.get('/ads/meta/pixels/'),
      api.get('/ads/meta/custom-conversions/').catch(() => ({ data: { conversions: [] } })),
      api.get(`/ads/campaigns/${campaignId}/meta-creative/`).catch(() => ({ data: { ads: [] } })),
    ])
      .then(([px, cv, adsRes]) => {
        setPixels(px.data.pixels || []);
        setConversions(cv.data.conversions || []);
        setAdStats((adsRes.data.ads || []).filter(a => a.metrics));
      })
      .catch(err => setError(err.response?.data?.error || t('conversions.metaConv.toasts.fetchError')));
  };
  useEffect(load, [campaignId]); // eslint-disable-line

  const createPixel = async () => {
    setCreating(true);
    try {
      await api.post('/ads/meta/pixels/', { name: 'Pixel' });
      toast(t('conversions.metaConv.toasts.pixelCreated'), 'success');
      load();
    } catch (err) {
      toast(err.response?.data?.error || t('conversions.metaConv.toasts.pixelError'), 'error');
    } finally { setCreating(false); }
  };

  const copySnippet = (pixelId) => {
    navigator.clipboard.writeText(snippetFor(pixelId)).then(() => {
      setCopiedId(pixelId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const createConversion = async () => {
    if (!convForm.name || !convForm.pixel_id || !convForm.url_contains) {
      toast(t('conversions.metaConv.toasts.convValidation'), 'error');
      return;
    }
    setSavingConv(true);
    try {
      await api.post('/ads/meta/custom-conversions/', convForm);
      toast(t('conversions.metaConv.toasts.convCreated'), 'success');
      setShowConvForm(false);
      setConvForm({ name: '', pixel_id: '', event: '', url_contains: '' });
      load();
    } catch (err) {
      toast(err.response?.data?.error || t('conversions.metaConv.toasts.convError'), 'error');
    } finally { setSavingConv(false); }
  };

  if (error) return <Text size="sm" c="dimmed" ta="center" py="md">{error}</Text>;
  if (pixels === null) return <Flex justify="center" py="xl"><Loader size="sm" color="brand" /></Flex>;

  const totalConv = adStats.reduce((acc, a) => acc + (a.metrics?.conversions || 0), 0);

  return (
    <Stack gap="md" style={{ maxWidth: 720 }}>
      {/* Wyniki tej kampanii */}
      <Paper withBorder p="md" radius="md">
        <Text size="sm" fw={600} mb={4}>{t('conversions.metaConv.campaignTitle')}</Text>
        {adStats.length > 0 ? (
          <>
            <Text size="xs" c="dimmed" mb="sm">{t('conversions.metaConv.totalLabel')} <b>{totalConv}</b> {t('conversions.metaConv.totalUnit')}</Text>
            <Stack gap={4}>
              {adStats.map(a => (
                <Group key={a.ad_id} gap="md">
                  <Text size="xs" style={{ minWidth: 220 }} truncate>{a.name}</Text>
                  <Text size="xs">{t('conversions.metaConv.convLabel')} <b className={a.metrics.conversions > 0 ? 'text-green-700' : ''}>{a.metrics.conversions}</b></Text>
                  {a.metrics.cost_per_result != null && (
                    <Text size="xs" c="dimmed">{t('conversions.metaConv.costPerConv')} {formatMoney(a.metrics.cost_per_result, currency)}</Text>
                  )}
                </Group>
              ))}
            </Stack>
          </>
        ) : (
          <Text size="xs" c="dimmed">{t('conversions.metaConv.emptyCampaign')}</Text>
        )}
      </Paper>

      {/* Pixel */}
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>{t('conversions.metaConv.pixelTitle')}</Text>
          {pixels.length === 0 && (
            <Button size="compact-xs" color="brand" leftSection={<Plus size={12} />} loading={creating} onClick={createPixel}>
              {t('conversions.metaConv.createPixel')}
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          {t('conversions.metaConv.pixelDescription')}
        </Text>
        {pixels.length === 0 ? (
          <Text size="xs" c="dimmed">{t('conversions.metaConv.emptyPixel')}</Text>
        ) : (
          <Stack gap="sm">
            {pixels.map(px => (
              <div key={px.id} className="border border-gray-100 rounded-md px-3 py-2">
                <Group justify="space-between">
                  <div>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>{px.name}</Text>
                      {px.last_fired_time
                        ? <Badge size="xs" variant="light" color="green">{t('conversions.metaConv.badgeActive')}</Badge>
                        : <Badge size="xs" variant="light" color="orange">{t('conversions.metaConv.badgeNoEvents')}</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed" ff="monospace">{px.id}</Text>
                  </div>
                  <Group gap="xs">
                    <Button size="compact-xs" variant="light" color="brand"
                      leftSection={copiedId === px.id ? <Check size={12} /> : <Copy size={12} />}
                      onClick={() => copySnippet(px.id)}>
                      {copiedId === px.id ? t('conversions.metaConv.copied') : t('conversions.metaConv.copyCode')}
                    </Button>
                    <Button size="compact-xs" variant="subtle" color="gray"
                      onClick={() => setShowSnippet(showSnippet === px.id ? null : px.id)}>
                      {showSnippet === px.id ? t('conversions.metaConv.hide') : t('conversions.metaConv.showCode')}
                    </Button>
                  </Group>
                </Group>
                {showSnippet === px.id && (
                  <pre className="mt-2 bg-gray-50 border border-gray-100 rounded p-2 text-[10px] leading-snug overflow-x-auto">{snippetFor(px.id)}</pre>
                )}
                {(px.events_7d || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {px.events_7d.slice(0, 6).map(ev => (
                      <span key={ev.event} className="text-[10px] bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">
                        {ev.event}: <b>{ev.count.toLocaleString('pl-PL')}</b> ({t('conversions.metaConv.sevenDays')})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Niestandardowe konwersje */}
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={600}>{t('conversions.metaConv.customTitle')}</Text>
          {pixels.length > 0 && (
            <Button size="compact-xs" variant="light" color="brand" leftSection={<Plus size={12} />} onClick={() => setShowConvForm(!showConvForm)}>
              {t('conversions.metaConv.addConversion')}
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed" mb="sm">{t('conversions.metaConv.customDescription')}</Text>
        {conversions.length > 0 ? (
          <Stack gap={4}>
            {conversions.map(cv => (
              <Group key={cv.id} gap="xs">
                <Text size="xs" fw={500}>{cv.name}</Text>
                <Text size="xs" c="dimmed">{cv.custom_event_type}</Text>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">{t('conversions.metaConv.emptyCustom')}</Text>
        )}
        {showConvForm && (
          <Stack gap="xs" mt="sm" p="sm" className="bg-gray-50 rounded-md">
            <TextInput size="xs" label={t('conversions.metaConv.form.name')} value={convForm.name}
              onChange={e => setConvForm({ ...convForm, name: e.target.value })} placeholder={t('conversions.metaConv.form.namePlaceholder')} />
            <Select size="xs" label={t('conversions.metaConv.form.pixel')} data={pixels.map(p => ({ value: p.id, label: `${p.name} (${p.id})` }))}
              value={convForm.pixel_id || null} onChange={v => setConvForm({ ...convForm, pixel_id: v || '' })} />
            <TextInput size="xs" label={t('conversions.metaConv.form.urlContainsRequired')} value={convForm.url_contains}
              onChange={e => setConvForm({ ...convForm, url_contains: e.target.value })} placeholder={t('conversions.metaConv.form.urlPlaceholder')} />
            <Select size="xs" label={t('conversions.metaConv.form.eventOptional')} clearable
              data={STANDARD_EVENTS.map(ev => ({ value: ev, label: ev }))}
              value={convForm.event || null} onChange={v => setConvForm({ ...convForm, event: v || '' })} />
            <Group gap="xs">
              <Button size="compact-xs" color="brand" loading={savingConv} onClick={createConversion}>{t('conversions.metaConv.form.create')}</Button>
              <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setShowConvForm(false)}>{t('conversions.metaConv.form.cancel')}</Button>
            </Group>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
